import { useLocation } from '@packages/router-dom';
import { usePrevious } from '../../../hooks/usePrevious';
import { useEffect } from 'react';
import { splitQuerySearchParams } from '../../../utils/url';
import { isUndefined } from '../../../predicates/type';

function useDifferentRouteCancellation() {
	const { pathname } = useLocation();
	const previousPathname = usePrevious(pathname);

	useEffect(() => {
		if (pathname === previousPathname || typeof previousPathname === 'undefined') {
			return;
		}

		window.__REACT__AXIOS_SOURCES__ = window.__REACT__AXIOS_SOURCES__.filter((i) => {
			const isDifferentPath = i.path !== pathname;
			const isPending = window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__.some((config) => {
				return config.signal === i.abortController?.signal;
			});

			if (isPending && isDifferentPath) {
				i.abortController?.abort();

				return false;
			}

			return true;
		});
	}, [pathname, previousPathname]);
}

const CANCELLATION_QUERY_PARAMS = ['organizationid', 'labelId'];

function containsAllowKey(querySearch: string, keys: Array<string>) {
	return keys.some((i) => querySearch.indexOf(i) !== -1);
}

function useDifferentParamsCancellation() {
	const { pathname, search } = useLocation();
	const previousPathname = usePrevious(pathname);
	const previousSearch = usePrevious(search);

	useEffect(() => {
		if (pathname !== previousPathname || typeof previousSearch === 'undefined' || search === previousSearch) {
			return;
		}

		const searchObject = splitQuerySearchParams(search, {
			toLowerCase: true,
		});
		const previousSearchObject = splitQuerySearchParams(previousSearch, {
			toLowerCase: true,
		});
		const searchParamsToCompare = CANCELLATION_QUERY_PARAMS.filter((i) => {
			const searchProp = searchObject[i] || '';
			const prevSearchProp = previousSearchObject[i] || '';

			return searchProp !== prevSearchProp;
		});

		if (searchParamsToCompare.length === 0) {
			return;
		}

		const searchStringEntries = searchParamsToCompare.map((i) => i.concat(`=${previousSearchObject[i]}`));

		window.__REACT__AXIOS_SOURCES__ = window.__REACT__AXIOS_SOURCES__.filter((i) => {
			const isSamePath = i.path === pathname;
			const isPending = window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__.some((config) => {
				return config.signal === i.abortController?.signal;
			});

			if (isPending && isSamePath && containsAllowKey(i.search, searchStringEntries)) {
				i.abortController?.abort();

				return false;
			}

			return true;
		});
	}, [pathname, previousPathname, previousSearch, search]);
}

/**
 * Property names in url search object that represent 'search' params.
 * */
const searchPropertyNames = ['search', 'whatSearch'];

/**
 * Cancels a query of the same pathname, but different url search parameter
 * */
function useDifferentSearchCancellation() {
	const { pathname, search } = useLocation();
	const previousPathname = usePrevious(pathname);
	const previousSearch = usePrevious(search);

	useEffect(() => {
		if (pathname !== previousPathname || typeof previousPathname === 'undefined' || search === previousSearch) {
			return;
		}

		const searchObject = splitQuerySearchParams(search, {
			toLowerCase: true,
		});
		const previousSearchObject = splitQuerySearchParams(previousSearch, {
			toLowerCase: true,
		});

		const searchProperty = Object.keys(searchObject).find((searchObjectKey) =>
			searchPropertyNames.some((searchProperty) => searchProperty.toLowerCase() === searchObjectKey),
		);

		if (isUndefined(searchProperty)) {
			return;
		}

		const searchValue = searchObject[searchProperty];
		const previousSearchValue = previousSearchObject[searchProperty];

		if (searchValue === previousSearchValue) {
			return;
		}

		window.__REACT__AXIOS_SOURCES__ = window.__REACT__AXIOS_SOURCES__.filter((i) => {
			const sourceSearchObject = splitQuerySearchParams(i.search, {
				toLowerCase: true,
			});

			const isPending = window.__REACT__PENDING_AXIOS_REQUEST_CONFIGS__.some((config) => {
				return config.signal === i.abortController?.signal;
			});
			const isSamePath = i.path === pathname;
			const isDifferentSearch = sourceSearchObject[searchProperty] !== searchObject[searchProperty];

			if (isPending && isSamePath && isDifferentSearch) {
				i.abortController?.abort();

				return false;
			}

			return true;
		});
	}, [pathname, previousPathname, previousSearch, search]);
}

export function useAPIRouteCancellation() {
	useDifferentRouteCancellation();
	useDifferentParamsCancellation();
	useDifferentSearchCancellation();
}
